<template>
	<div class="p-grid">
		<div class="p-col-12">
			<div class="card">
        <Toolbar class="p-mb-4 p-p-2">
          <template #start>
            <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('Tickets') }}</div>
          </template>
          <template #end>
            <Button v-if="showUnanswered"
                    :icon="'pi pi-bars'"
                    :label="$t('Show all')"
                    @click="showAll"
                    class="p-button-raised"/>
          </template>
        </Toolbar>

        <Spinner v-if="isLoading"/>
        <DesktopDataTable v-else
                          :dataTableIsLoading="dataTableIsLoading"
                          :showHeader="false"
                          :itemsArrayIsEmpty="itemsArrayIsEmpty"
                          :sort="sort"
                          :showSearchInput="false"
                          :actionButtonsCount="actionButtonsCount"
                          @sort-items="sortItems"
                          :headers="[
              {name: 'id', title: 'ID',sortable: false},
              {name: 'subject', title: $t('Subject'),sortable: false},
              {name: 'tenant', title: $t('Tenant'),sortable: false},
              {name: 'location', title: $t('Location'),sortable: false},
              {name: 'type', title: $t('Type'), sortable: false},
              {name: 'status', title: $t('Status'), sortable: false},
              {name: 'messages_count', title: $t('Messages'), sortable: false},
              {name: 'created_at', title: $t('Created'), sortable: true},
          ]">
          <template v-slot:body>
            <tr v-for="item of items"
                :key="item.id"
                :class="{'updated-item': isItemUpdated(item.id)}">

              <td>
                <span v-if="item.id">{{ item.id }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.subject">{{ item.subject }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.tenant_name">{{ item.tenant_name }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.location_name">{{ item.location_name }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <span v-if="item.type">{{ formatType(item.type) }}</span>
                <i v-else class="ti-minus"/>
              </td>

              <td>
                <div v-if="ticketStateLabel(item)" class="p-d-flex p-flex-wrap p-ai-center">
                  <div class="state-badge" :class="ticketStateColor(item)">
                    {{ ticketStateLabel(item) }}
                  </div>
                </div>
                <i v-else class="ti-minus"/>

                <div v-if="Number(item.is_deleted_by_user) === 1" class="table-date-line p-text-secondary">
                  {{ $t('Deleted') }}:
                  <span v-if="item.deleted_by_user_at">{{ formatBy(item.deleted_by_user_at) }}</span>
                  <i v-else class="ti-minus"/>
                </div>
              </td>

              <td>
                <div class="messages-counts">
                  <div>{{ item.messages_count || 0 }}</div>
                  <div class="p-badge p-badge-danger"
                       v-tooltip.top="$t('unreadMessagesTooltip')"
                       v-if="item.unread_messages_count && +item.unread_messages_count">
                    {{ item.unread_messages_count || 0 }}
                  </div>
                </div>

              </td>

              <td class="table-date-line">
                <span v-if="item.created_at">
                  {{ formatBy(item.created_at, item.creator) }}
                </span>
                <i v-else class="ti-minus"/>
              </td>

              <td v-show="actionButtonsCount">
                <div class="actions">
                  <router-link :to="{ path: `/admin/support/tickets/${item.id}` }" draggable="false">
                    <ViewButton/>
                  </router-link>
                  <DeleteButton @click="confirmItemDelete(item)"/>
                </div>
              </td>
            </tr>
          </template>
        </DesktopDataTable>


        <OwnPaginator v-show="items && items.length"
                      :rows="pagination.perPage"
                      :currentPage="pagination.currentPage - 1"
                      :totalPages="pagination.pageCount"
                      :totalRecords="pagination.totalCount"
                      :rowsPerPageOptions="[5,10,25,50,100,200]"
                      :pageLinkSize="11"/>

        <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

        <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                            :modalDataIsLoading="modalDataIsLoading"
                            @close="closeConfirmItemDeleteModal"
                            @delete-item="deleteTicket"/>
			</div>

		</div>
	</div>
</template>

<script>
import httpClient from '@/services/http.services'
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import formatMixins from "@/mixins/formatMixins";
import TicketStates from "@/translations/states/TicketStates";


export default {
  mixins: [ datatableMixins, showErrorsMixins, formatMixins ],
	data() {
		return {
      STATUS_CLOSED: 2,
      fieldsExpand: 'creator,messages_count,tenant_name,location_name,unread_messages_count',
      apiLink: 'ticket/admin-index',
      deleteItemUrl: 'ticket/delete-ticket',
      routePath: '/admin/support/tickets',
      TicketStates
		}
	},
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    this.checkRouteQuery()
    this.getPageData()
	},
  methods: {
    getPageData() {
      this.getItems()
    },
    async getItems() {
      if (!this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('tickets')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
            unanswered: this.showUnanswered ? 1 : 0,
          },
          signal: ctrl?.signal
        })
        if (status === 200 && data && Array.isArray(data)) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }

          this.items = data

          if (!data.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        this.items = []
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.dataTableIsLoading = false
        this.isLoading = false
        if (ctrl && this._abortors?.tickets === ctrl) {
          this._abortors.tickets = null
        }
      }
    },
    ticketStateColor(item) {
      return this.resolveTicketDisplayState(item)?.color || ''
    },

    ticketStateLabel(item) {
      const state = this.resolveTicketDisplayState(item)
      return state?.label?.[this.$i18n.locale] || state?.label?.en || ''
    },
    async deleteTicket() {
      const res = await this.deleteItem()

      if (res?.success) {
        this.$store.commit('updateUnansweredTicketsCount', res.unanswered_tickets_count ?? 0)
        this.$store.commit('updateAnsweredTicketsCount', res.answered_tickets_count ?? 0)
      }
    },
    resolveTicketDisplayState(item) {
      if (!item) return null

      if (Number(item.is_deleted_by_user) === 1) {
        return this.TicketStates.find(s => s.id === 7) || null
      }

      if (Number(item.status) === this.STATUS_CLOSED) {
        return this.TicketStates.find(s => s.id === 5) || null
      }


      return this.TicketStates.find(s => s.id === Number(item.state)) || null
    },
    formatType(type) {
      switch (Number(type)) {
        case 1:
          return this.$t('Question')
        case 3:
          return this.$t('Bug')
        case 5:
          return this.$t('Idea')
        case 7:
          return this.$t('Feature')
        default:
          return type
      }
    },
	},
  computed: {
    showUnanswered() {
      return this.$route.query?.unanswered === '1' ||
          this.$route.query?.unanswered === 'true'
    },
    actionButtonsCount() {
      return 2
    },
  }
}
</script>

<style scoped lang="scss">
.messages-counts {
  display: flex;
  align-items: center;
  gap: 12px;
}
</style>